import React, { createContext, useContext, useState, useEffect } from "react";
import { PublicClientApplication, InteractionRequiredAuthError } from "@azure/msal-browser";
import { msalConfig, loginRequest, tokenRequest } from "../utils/authConfig";
import authService from "../services/authService";
import { resolveSession } from "./sessionRestore";
import { useNavigate, useLocation } from "react-router-dom";

// Force development mode with true authentication
const isDevelopment = process.env.NODE_ENV === 'development';
const skipAuth = isDevelopment && (process.env.REACT_APP_SKIP_AUTH === 'true');
// Add option to use Entra ID in development
const useEntraInDev = isDevelopment && (process.env.REACT_APP_USE_ENTRA_IN_DEV === 'true');

console.log('AuthContext initializing:');
console.log('Environment:', process.env.NODE_ENV);
console.log('Skip Auth Setting:', process.env.REACT_APP_SKIP_AUTH);
console.log('Use Entra ID in Dev:', process.env.REACT_APP_USE_ENTRA_IN_DEV);
console.log('Skip Auth Active:', skipAuth);
console.log('Entra ID in Dev Active:', useEntraInDev);

// Initialize MSAL instance
let msalInstance = null;
let msalInitialized = false;
let msalInitPromise = null;
let loginInProgress = false;

const initializeMsal = async () => {
  // Skip MSAL initialization in development mode, but only if not using Entra ID in dev
  if (skipAuth && !useEntraInDev) {
    console.log("Skipping MSAL initialization in development mode");
    return true;
  }
  
  // If there's already an initialization in progress, return that promise
  if (msalInitPromise) {
    return msalInitPromise;
  }
  
  // Create a new initialization promise
  msalInitPromise = new Promise(async (resolve) => {
    try {
      console.log("Initializing MSAL instance with config:", JSON.stringify(msalConfig));
      msalInstance = new PublicClientApplication(msalConfig);
      await msalInstance.initialize();
      console.log("MSAL instance initialized successfully");
      msalInitialized = true;
      resolve(true);
    } catch (error) {
      console.error("Failed to initialize MSAL:", error);
      resolve(false);
    }
  });
  
  return msalInitPromise;
};

// Start initialization immediately but don't wait for it
if (!skipAuth || useEntraInDev) {
  initializeMsal();
}

// Authentication context
const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  // In development mode with SKIP_AUTH, force authentication to true, but only if not using Entra ID
  const [account, setAccount] = useState((skipAuth && !useEntraInDev) ? { name: 'Development User', username: 'dev@example.com' } : null);
  const [isAuthenticated, setIsAuthenticated] = useState(skipAuth && !useEntraInDev);
  const [loading, setLoading] = useState(!(skipAuth && !useEntraInDev));
  const [authError, setAuthError] = useState(null);
  const [redirectRequired, setRedirectRequired] = useState(false); // Track if navigation is needed
  const navigate = useNavigate();
  const location = useLocation();

  // Handle redirects after auth state changes
  useEffect(() => {
    // If we need to redirect to dashboard, do it only when auth loading is complete
    // Only redirect if we're on login/root pages, not when navigating to other routes
    if (redirectRequired && !loading && isAuthenticated) {
      const currentPath = location.pathname;
      // Only redirect if we're on login or root, not on other valid routes
      if (currentPath === '/' || currentPath === '/login' || currentPath === '/dev-login') {
        console.log("AuthContext - Redirect required flag detected, navigating to dashboard from", currentPath);
        setRedirectRequired(false); // Reset flag after handling
        
        // Use a slight delay to ensure state is properly updated before navigation
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 50);
      } else {
        // If we're already on a valid route, just clear the flag
        console.log("AuthContext - Redirect flag set but already on valid route:", currentPath);
        setRedirectRequired(false);
      }
    }
  }, [redirectRequired, loading, isAuthenticated, navigate, location.pathname]);

  // Initialize auth state
  //
  // Runs ONCE per page load. It previously depended on `location.pathname`,
  // so every navigation re-entered the whole MSAL handshake — a large part of
  // why a refresh (and even in-app navigation) kept bouncing through SSO.
  useEffect(() => {
    // Skip entirely in development mode with skipAuth, but not if using Entra ID in dev
    if (skipAuth && !useEntraInDev) {
      console.log('Development mode: Skipping authentication');
      return;
    }

    let cancelled = false;

    const initialize = async () => {
      try {
        // Ensure MSAL is initialized
        if (!msalInitialized) {
          console.log("MSAL not yet initialized, waiting for initialization...");
          const success = await initializeMsal();
          if (!success) {
            setAuthError("Microsoft authentication failed to initialize");
            setLoading(false);
            return;
          }
        }

        // NOTE: we deliberately do NOT clear msal.* keys here any more.
        // The previous code deleted every localStorage key matching
        // /msal/ && /interaction|error/ on each run, which destroyed the very
        // cache that makes a refresh silent and forced a new SSO round trip.

        let redirectAccount = null;
        try {
          const response = await msalInstance.handleRedirectPromise();
          if (response) redirectAccount = response.account;
        } catch (redirectError) {
          console.error("Error handling redirect:", redirectError);
          // A failed redirect parse must not wipe an otherwise good cached
          // session; fall through to the cache check below.
        }

        if (cancelled) return;

        const session = await resolveSession({
          redirectAccount,
          accounts: msalInstance.getAllAccounts(),
          acquireApiToken: (acct) => acquireApiToken(acct, { allowPopup: false }),
          verifyToken: async (token) => {
            if (isDevelopment) return true;
            return verifyTokenWithBackend(token);
          },
        });

        if (cancelled) return;

        if (session.apiToken) authService.setAuthHeader(session.apiToken);
        setAccount(session.account);
        setIsAuthenticated(session.isAuthenticated);

        if (session.isAuthenticated && session.degraded) {
          console.warn("Session restored without a verified API token; will retry per request.");
        }

        if (session.isAuthenticated) {
          const path = window.location.pathname;
          if (path === '/' || path === '/login') setRedirectRequired(true);
        }
      } catch (error) {
        console.error("Authentication initialization error:", error);
        setAuthError("Authentication initialization error: " + error.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    initialize();
    return () => {
      cancelled = true;
    };
    // Intentionally empty: this must run once per load, not per navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Function to verify token with backend
  const verifyTokenWithBackend = async (token) => {
    if (skipAuth) return true;
    
    try {
      console.log("Verifying token with backend");
      const response = await authService.checkAuth(token);
      return response.authenticated;
    } catch (error) {
      console.error("Token verification error:", error);
      return false;
    }
  };

  /**
   * Acquire an API-scoped access token.
   *
   * `allowPopup` is false during silent session restore: popping a window on
   * page load is exactly the "SSO on every refresh" behaviour we are fixing,
   * and browsers block unsolicited popups anyway. Interactive callers (the
   * login button, an explicit retry) pass allowPopup: true.
   */
  const acquireApiToken = async (accountToUse, { allowPopup = true } = {}) => {
    if (!msalInstance || !accountToUse) {
      console.error("Cannot acquire API token - MSAL instance or account missing");
      return null;
    }

    try {
      const tokenResponse = await msalInstance.acquireTokenSilent({
        ...tokenRequest,
        account: accountToUse,
      });
      return tokenResponse.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        if (!allowPopup) throw error; // let resolveSession decide
        try {
          const tokenResponse = await msalInstance.acquireTokenPopup({
            ...tokenRequest,
            account: accountToUse,
          });
          return tokenResponse.accessToken;
        } catch (popupError) {
          console.error("Error acquiring API token via popup:", popupError);
          return null;
        }
      }
      console.error("Error acquiring API token silently:", error);
      return null;
    }
  };

  // Login function
  const login = async () => {
    // Prevent concurrent login attempts
    if (loginInProgress) {
      console.log("Login already in progress, ignoring duplicate request");
      return;
    }
    
    // If in development mode with auth skipped, simulate successful login
    if (skipAuth && !useEntraInDev) {
      console.log('Development mode: Simulating login');
      setAccount({ name: 'Development User', username: 'dev@example.com' });
      setIsAuthenticated(true);
      
      // Use the redirect flag pattern
      setRedirectRequired(true);
      return;
    }

    try {
      loginInProgress = true;
      setLoading(true);
      setAuthError(null);
      
      // Ensure MSAL is initialized before login
      console.log("Ensuring MSAL is initialized before login...");
      if (!msalInitialized) {
        const success = await initializeMsal();
        if (!success) {
          throw new Error("Microsoft authentication failed to initialize");
        }
      }
      
      // Double check that msalInstance exists
      if (!msalInstance) {
        throw new Error("MSAL instance is null after initialization");
      }
      
      // Clear any existing MSAL interaction state
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith("msal.interaction")) {
          console.log(`Clearing MSAL key before login: ${key}`);
          sessionStorage.removeItem(key);
        }
      });
      
      console.log("Attempting login with popup instead of redirect");
      
      try {
        // Try popup login first as it tends to have fewer issues with interaction state
        const authResult = await msalInstance.loginPopup(loginRequest);
        console.log("Login popup completed successfully");
        
        if (authResult) {
          console.log("Setting account from popup login");
          setAccount(authResult.account);
          
          // Verify token with backend if needed
          if (!isDevelopment) {
            const apiToken = await acquireApiToken(authResult.account);
            if (!apiToken) {
              throw new Error("Unable to acquire API token");
            }
            const isValid = await verifyTokenWithBackend(apiToken);
            if (isValid) {
              authService.setAuthHeader(apiToken);
            } else {
              throw new Error("Backend token verification failed");
            }
          }
          
          setIsAuthenticated(true);
          setLoading(false);
          
          // Set redirect flag to go to dashboard
          setRedirectRequired(true);
        }
      } catch (popupError) {
        // If popup fails (often due to popup blockers), fall back to redirect
        console.warn("Popup login failed, falling back to redirect:", popupError);
        
        // Force clear any state before trying redirect
        sessionStorage.removeItem("msal.interaction.status");
        localStorage.removeItem("msal.interaction.status");
        
        // Use redirect as fallback
        await msalInstance.loginRedirect({
          ...loginRequest,
          redirectStartPage: window.location.href
        });
      }
    } catch (error) {
      console.error("Login error:", error);
      setAuthError(error.message || "Authentication failed");
      setLoading(false);
      throw error;
    } finally {
      loginInProgress = false;
    }
  };

  // Logout function
  const logout = () => {
    // If in development mode with auth skipped, simulate logout
    if (skipAuth && !useEntraInDev) {
      console.log('Development mode: Simulating logout');
      // In dev mode, we actually don't log out - just simulate it
      setTimeout(() => {
        setAccount({ name: 'Development User', username: 'dev@example.com' });
        setIsAuthenticated(true);
      }, 500);
      return;
    }

    console.log("Logout initiated - Comprehensive logout process starting");
    
    // Step 1: Clear React state immediately
    setIsAuthenticated(false);
    setAccount(null);
    authService.setAuthHeader(null);
    
    // Step 2: Clear all MSAL-related cache first
    try {
      // Clear localStorage
      for (const key in localStorage) {
        if (key.startsWith('msal.')) {
          console.log('Clearing localStorage item:', key);
          localStorage.removeItem(key);
        }
      }
      
      // Clear sessionStorage
      for (const key in sessionStorage) {
        if (key.startsWith('msal.')) {
          console.log('Clearing sessionStorage item:', key);
          sessionStorage.removeItem(key);
        }
      }
      
      // Also clear cookies that might be related to auth
      document.cookie.split(';').forEach(cookie => {
        const trimmedCookie = cookie.trim();
        if (trimmedCookie.startsWith('msal.') || trimmedCookie.startsWith('MSAL')) {
          const name = trimmedCookie.split('=')[0];
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
          console.log(`Cleared cookie: ${name}`);
        }
      });
    } catch (e) {
      console.warn("Error clearing cache:", e);
    }
    
    // Step 3: Try to log out with MSAL if available, but don't wait for it
    if (msalInstance) {
      try {
        // Get all accounts to ensure proper logout
        const accounts = msalInstance.getAllAccounts();
        console.log(`Found ${accounts.length} accounts to logout`);
        
        const logoutRequest = {
          // Must follow the actual origin: hardcoding localhost meant the
          // production logout redirect pointed at the developer's machine.
          postLogoutRedirectUri: `${window.location.origin}/login`,
          onRedirectNavigate: () => {
            // Prevent MSAL from navigating - we'll do it ourselves
            console.log("Intercepting redirect navigate from MSAL");
            return false;
          }
        };
        
        // Start the MSAL logout process but don't wait for it
        console.log("Initiating MSAL logout with settings:", logoutRequest);
        msalInstance.logoutRedirect(logoutRequest).catch(e => {
          console.warn("MSAL logout error (non-blocking):", e);
        });
      } catch (error) {
        console.warn("Error during MSAL logout (non-blocking):", error);
      }
    }
    
    // Step 4: Force navigation to login page regardless of MSAL logout success
    console.log("Forcing navigation to login page");
    setTimeout(() => {
      console.log("Executing navigation to login");
      window.location.href = '/login'; // Use direct location change instead of navigate for complete page reset
    }, 300);
  };

  // Get token for API calls
  const getToken = async () => {
    // If in development mode with auth skipped, return a dummy token
    if (skipAuth) {
      console.log('Development mode: Returning dummy token');
      return 'dev-mode-dummy-token';
    }

    if (!msalInstance) {
      console.error("Cannot get token - MSAL instance is null");
      return null;
    }

    if (!account) return null;
    
    try {
      const apiToken = await acquireApiToken(account);
      if (apiToken) {
        authService.setAuthHeader(apiToken);
      }
      return apiToken;
    } catch (error) {
      console.error("Error acquiring token:", error);
      return null;
    }
  };

  const value = {
    account,
    isAuthenticated,
    loading,
    authError,
    login,
    logout,
    getToken,
    msalInstance,
    devMode: skipAuth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}; 
